import React, { useState, useEffect } from "react";
import { Search, Phone, Info } from "lucide-react";

export default function Menu() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/menu_items")
      .then((res) => res.json())
      .then((data) => {
        setMenuItems(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch menu items", err);
        setLoading(false);
      });
  }, []);

  // Group items by category
  const categoryOrder = ["Smørrebrød", "Platter", "Sandwich", "Div.", "Burger", "Drikkevarer"];
  
  const groupedCategories = categoryOrder.map(catName => {
    let items = menuItems.filter(item => item.category === catName);
    if (catName === "Smørrebrød") {
      const nonSild = items.filter(item => !item.name.toLowerCase().includes("sild"));
      const sild = items.filter(item => item.name.toLowerCase().includes("sild"));
      items = [...nonSild, ...sild];
    }
    return {
      id: catName.toLowerCase().replace(/[^a-z0-9]/g, ""),
      title: catName.toUpperCase(),
      items: items
    };
  }).filter(cat => cat.items.length > 0);

  const filteredCategories = groupedCategories.map((category) => {
    const filteredItems = category.items.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    return { ...category, items: filteredItems };
  }).filter((category) => {
    if (activeCategory !== "all" && category.id !== activeCategory) return false;
    return category.items.length > 0;
  });

  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      {/* Page Header */}
      <section className="bg-black text-white py-16 border-b border-neutral-900 text-center relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/85c2c89a-00bc-4aa6-a723-ddfe1a3b4f01.jpg"
            alt="Menukort baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-neutral-400 block mb-2">
            Frisklavet hver dag
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight">
            Vores Menukort
          </h1>
          <p className="text-sm text-neutral-400 max-w-lg mx-auto leading-relaxed">
            Alt vores smørrebrød laves på hjemmelavet rugbrød af friske råvarer. Vores burgere er lavet af friskhakket oksekød. Ring og bestil på forhånd!
          </p>
        </div>
      </section>

      {/* Filter & Search Bar */}
      <section className="py-8 border-b border-neutral-200 sticky top-[73px] lg:top-[137px] bg-white z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row gap-4 justify-between items-center">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 justify-center md:justify-start w-full md:w-auto">
            <button
              onClick={() => handleCategoryChange("all")}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                activeCategory === "all"
                  ? "bg-black text-white"
                  : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
            >
              Alle
            </button>
            {groupedCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                  activeCategory === cat.id
                    ? "bg-black text-white"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                }`}
              >
                {cat.title}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} />
            <input
              type="text"
              placeholder="Søg i menukortet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-neutral-200 text-sm focus:outline-none focus:border-neutral-900"
            />
          </div>
        </div>
      </section>

      {/* Menu Content */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-neutral-500 text-sm">Indlæser menukort...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-neutral-500 text-sm">Ingen retter matchede din søgning.</p>
            <button
              onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
              className="mt-4 text-xs font-bold uppercase tracking-wider text-[#2d5a27] hover:underline"
            >
              Nulstil filtre
            </button>
          </div>
        ) : (
          <div className="space-y-20">
            {filteredCategories.map((category) => (
              <div key={category.id} className="scroll-mt-36" id={category.id}>
                {/* Category Header */}
                <div className="border-b border-neutral-200 pb-4 mb-8">
                  <h2 className="text-2xl md:text-3xl font-serif font-bold text-neutral-900">
                    {category.title}
                  </h2>
                </div>

                {/* Items Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                  {category.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col justify-between py-3 border-b border-neutral-100 hover:bg-neutral-50/50 px-2 transition-colors"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex gap-3">
                          <div>
                            <h3 className="font-serif font-bold text-sm text-neutral-900 flex flex-wrap items-center gap-2">
                              <span>{item.name}</span>
                            </h3>
                            {item.name.toLowerCase().includes("sild") && (
                              <div className="text-[11px] text-neutral-500/85 font-sans font-normal mt-0.5 flex items-center gap-1">
                                <i className="fa-solid fa-snowflake text-[9px]" /> Sæsonvare: Bestilles typisk op til jul
                              </div>
                            )}
                            {item.description && (
                              <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </div>
                        {category.id === "smrrebrd" ? (
                          <div className="text-right shrink-0">
                            <div className="font-mono text-sm font-bold text-neutral-900">
                              {item.name.toLowerCase().includes("fisk") ? "28 kr." : "24 kr."} <span className="text-[10px] text-neutral-500 font-sans font-normal">(Håndmad)</span>
                            </div>
                            <div className="font-mono text-xs text-neutral-500 mt-0.5">
                              58 kr. <span className="text-[10px] text-neutral-400 font-sans font-normal">(Luksus)</span>
                            </div>
                          </div>
                        ) : item.price && (
                          <span className="font-mono text-sm font-bold text-neutral-900 shrink-0">
                            {item.price} kr.
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Order CTA */}
      <section className="py-16 bg-neutral-50 border-t border-neutral-200 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-4">
            Bestil på forhånd til afhentning
          </h3>
          <p className="text-sm text-neutral-600 mb-8 max-w-md mx-auto">
            Undgå ventetid i butikken. Ring til os på telefonen, eller bestil online, så har vi din bestilling klar til dig, når du kommer.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/bestilling"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-semibold uppercase tracking-wider bg-black hover:bg-neutral-800 text-white transition-all duration-300"
            >
              <span>Bestil Online</span>
            </a>
            <a
              href="tel:48266613"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-semibold uppercase tracking-wider bg-[#2d5a27] hover:bg-[#20401c] text-white transition-all duration-300"
            >
              <Phone size={14} />
              <span>Ring og bestil: 48 26 66 13</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
